"use client";

import JsBarcode from "jsbarcode";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { ProductRow } from "@/lib/types/product";

type Props = { products: ProductRow[] };

export const ProductLabelSheet = forwardRef<HTMLDivElement, Props>(
  function ProductLabelSheet({ products }, ref) {
    const innerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    useEffect(() => {
      if (!innerRef.current) {
        return;
      }
      products.forEach((p) => {
        const svg = innerRef.current?.querySelector<SVGElement>(
          `svg[data-product-id="${p.id}"]`,
        );
        if (!svg) {
          return;
        }
        const code = String(p.barcode || p.sku || p.id);
        try {
          JsBarcode(svg, code, {
            format: "CODE128",
            width: 1.1,
            height: 32,
            displayValue: true,
            fontSize: 10,
            margin: 0,
          });
        } catch {
          /* ignore invalid symbology */
        }
      });
    }, [products]);

    return (
      <div ref={innerRef} className="bg-white text-black">
        {products.map((p) => (
          <div
            key={p.id}
            className="box-border w-[58mm] min-h-[40mm] break-inside-avoid border-b border-dashed border-gray-300 p-2 print:border-0"
          >
            <svg data-product-id={p.id} className="mx-auto block h-12 max-w-full" />
            <p className="mt-1 line-clamp-2 text-center text-[10px] font-medium leading-tight">
              {p.name}
            </p>
            <p className="mt-0.5 text-center font-mono text-xs font-semibold">
              ₹{Number(p.selling_price).toFixed(2)}
            </p>
            <p className="text-center font-mono text-[9px] text-gray-600">
              MRP ₹{Number(p.mrp).toFixed(2)}
            </p>
          </div>
        ))}
      </div>
    );
  },
);
